import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MotifDatePicker, MotifToast } from "@ey-xd/motif-react";
import { ExportIcon } from "../../assets/Icons/ExportIcon";
import FilterFunnelIcon from "../../assets/Icons/FilterFunnelIcon";
import { SearchIcon } from "../../assets/Icons/SearchIcon";
import { FileEmptyPage } from "../../assets/Icons/EmptyPage";
import {
  QUALITY_FRAMEWORK_TABS,
  CELL_TRUNCATE_LENGTH,
  buildTabCounts,
  canCreateQualityRequest,
  hasQualityFrameworkAccess,
  filterRequests,
  mapQualityRequest,
  mapQualityRequestForExport,
  truncateText,
} from "./qualityFramework.utils";
import "./QualityFramework.scss";
import { QualityFrameworkAccessDenied } from "./QualityFrameworkAccessDenied";
import { DownloadIcon } from "../../assets/Icons/DownloadIcon";
import { PlayListPlusIcon } from "../../assets/Icons/PlayListPlusIcon";
import { PageStarIcon } from "../../assets/Icons/PageStarIcon";
import { routes } from "../../routes/routes.constatns";
import { QualityFrameworkService } from "../../api/services";
import { triggerQualityFrameworkRefresh } from "../../store/slices/qualityFrameworkSlice";
import { LoadingIndicator } from "../CommonUIComponents/LoadingIndicator/LoadingIndicator";
import { Tooltip } from "../CommonUIComponents/Tooltip/Tooltip";
import { formatDateForEditDisplay } from "../../util/dateUtil";
import { createExcelFile } from "../../util/excelUtil";
import { CloseIcon } from "../../assets/Icons/CloseIcon";
import { CalendarIcon } from "../../assets/Icons/Calendar";
import { NavDownArrow } from "../../assets/Icons/NavArrowDown";
import { MoreVertical } from "../../assets/Icons/MoreVertical";

const createEmptyFilters = () => ({
  startDate: null,
  endDate: null,
  assignedTo: [],
  clientName: [],
  engagementName: [],
  axonId: [],
  area: [],
  superRegion: [],
  country: [],
});

const uniqueValues = (requests, property) =>
  [...new Set(requests.map((request) => request[property]).filter((value) => value && value !== "-"))]
    .sort((first, second) => String(first).localeCompare(String(second)));

const parseRequestDate = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const renderTruncatedCell = (value) => {
  const text = String(value ?? "");

  if (text.length <= CELL_TRUNCATE_LENGTH) {
    return text;
  }

  return (
    <Tooltip
      trigger={<span className="qf-truncated">{truncateText(text)}</span>}
      tooltipText={text}
      placement="top"
    />
  );
};

export const QualityFramework = () => {
  const selectedRole = useSelector((state) => state.user?.selectedRole);
  const userRoles = useSelector((state) => state.user?.userRoles);
  const rolesLoaded = useSelector((state) => state.user?.rolesLoaded);
  const hasAccess = hasQualityFrameworkAccess(userRoles);
  const refreshId = useSelector((state) => state.qualityFramework?.refreshId ?? 0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("all");
  const [searchText, setSearchText] = useState("");

  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState({
    dateRange: true,
    assignedTo: false,
    clientName: false,
    engagementName: false,
    axonId: false,
    area: false,
    superRegion: false,
    country: false,
  });
  const [tempFilters, setTempFilters] = useState(createEmptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(createEmptyFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    let isActive = true;

    if (!rolesLoaded || !hasAccess) {
      setIsLoading(false);
      return undefined;
    }

    const loadRequests = async () => {
      try {
        setIsLoading(true);
        const response = await QualityFrameworkService.getRequests();
        const orders = response?.data?.orders || [];
        if (isActive) {
          setRequests(orders.map(mapQualityRequest));
        }
      } catch (error) {
        console.error("Error fetching quality framework requests:", error);
        if (isActive) {
          setRequests([]);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadRequests();

    return () => {
      isActive = false;
    };
  }, [refreshId, rolesLoaded, hasAccess]);

  useEffect(() => {
    if (openActionMenuId === null) return undefined;

    const closeMenu = (event) => {
      if (!event.target.closest?.(".qf-action-menu-wrapper")) {
        setOpenActionMenuId(null);
      }
    };

    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, [openActionMenuId]);

  useEffect(() => {
    if (!deleteError) return undefined;

    const timerId = setTimeout(() => setDeleteError(""), 5000);
    return () => clearTimeout(timerId);
  }, [deleteError]);

  const toggleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAll = () =>
    setSelectedIds((prev) =>
      prev.length === paginatedRequests.length ? [] : paginatedRequests.map((r) => r.id)
    );

  const canCreateRequest = useMemo(
    () => canCreateQualityRequest(selectedRole, userRoles),
    [selectedRole, userRoles],
  );

  const filterOptions = useMemo(() => ({
    assignedTo: uniqueValues(requests, "assignedTo"),
    clientName: uniqueValues(requests, "clientName"),
    engagementName: uniqueValues(requests, "engagementName"),
    axonId: uniqueValues(requests, "orderRequestId"),
    area: uniqueValues(requests, "area"),
    superRegion: uniqueValues(requests, "superRegion"),
    country: uniqueValues(requests, "countryName"),
  }), [requests]);

  const requestsMatchingFilters = useMemo(() => {
    let result = filterRequests(requests, "all", searchText);

    ["assignedTo", "clientName", "engagementName", "area", "superRegion", "country"].forEach((filterKey) => {
      if (appliedFilters[filterKey].length > 0) {
        const property = filterKey === "country" ? "countryName" : filterKey;
        result = result.filter((request) => appliedFilters[filterKey].includes(request[property]));
      }
    });

    if (appliedFilters.axonId.length > 0) {
      result = result.filter((request) => appliedFilters.axonId.includes(request.orderRequestId));
    }

    if (appliedFilters.startDate || appliedFilters.endDate) {
      const startDate = appliedFilters.startDate ? new Date(appliedFilters.startDate).setHours(0, 0, 0, 0) : null;
      const endDate = appliedFilters.endDate ? new Date(appliedFilters.endDate).setHours(23, 59, 59, 999) : null;
      result = result.filter((request) => {
        const requestDate = parseRequestDate(request.deliveryDate);
        if (!requestDate) {
          return false;
        }
        const timestamp = requestDate.getTime();
        return (!startDate || timestamp >= startDate) && (!endDate || timestamp <= endDate);
      });
    }

    return result;
  }, [requests, searchText, appliedFilters]);

  const tabCounts = useMemo(
    () => buildTabCounts(requestsMatchingFilters),
    [requestsMatchingFilters],
  );

  const filteredRequests = useMemo(
    () => filterRequests(requestsMatchingFilters, activeTab),
    [requestsMatchingFilters, activeTab],
  );

  // Reset to page 1 whenever the tab, search, or filters change the result set
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchText, appliedFilters]);

  const totalPages = Math.ceil(filteredRequests.length / pageSize) || 1;
  const paginatedRequests = useMemo(
    () =>
      filteredRequests.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      ),
    [filteredRequests, currentPage, pageSize],
  );

  // Keep current page in bounds if the result set shrinks
  useEffect(() => {
    if (currentPage > 1 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleTabClick = (tabKey) => {
    setActiveTab(tabKey);
  };

  const toggleFilterPanel = () => {
    if (!isFilterPanelOpen) {
      setTempFilters({
        ...appliedFilters,
        assignedTo: [...appliedFilters.assignedTo],
        clientName: [...appliedFilters.clientName],
        engagementName: [...appliedFilters.engagementName],
        axonId: [...appliedFilters.axonId],
        area: [...appliedFilters.area],
        superRegion: [...appliedFilters.superRegion],
        country: [...appliedFilters.country],
      });
    }
    setIsFilterPanelOpen((isOpen) => !isOpen);
  };

  const toggleFilterAccordion = (filterKey) => {
    setExpandedFilters((filters) => ({ ...filters, [filterKey]: !filters[filterKey] }));
  };

  const handleDateChange = (value) => {
    if (!value) {
      setTempFilters((filters) => ({ ...filters, startDate: null, endDate: null }));
      return;
    }
    const [startDate, endDate] = Array.isArray(value) ? value : [value, null];
    setTempFilters((filters) => ({
      ...filters,
      startDate: startDate instanceof Date ? startDate : null,
      endDate: endDate instanceof Date ? endDate : null,
    }));
  };

  const toggleFilterValue = (filterKey, value) => {
    setTempFilters((filters) => ({
      ...filters,
      [filterKey]: filters[filterKey].includes(value)
        ? filters[filterKey].filter((selectedValue) => selectedValue !== value)
        : [...filters[filterKey], value],
    }));
  };

  const toggleAllFilterValues = (filterKey) => {
    const options = filterOptions[filterKey];
    setTempFilters((filters) => ({
      ...filters,
      [filterKey]: filters[filterKey].length === options.length ? [] : [...options],
    }));
  };

  const clearFilters = () => setTempFilters(createEmptyFilters());

  const applyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setSelectedIds([]);
    setIsFilterPanelOpen(false);
  };

  const selectedFilterCount = Object.entries(tempFilters).reduce(
    (count, [filterKey, value]) => count + (Array.isArray(value) ? value.length : value ? 1 : 0),
    0,
  );

  const handleDeleteRecord = async (row) => {
    try {
      setDeletingId(row.orderRequestId);
      setDeleteError("");
      await QualityFrameworkService.deleteRecord(row.orderRequestId);
      setSelectedIds((prev) => prev.filter((id) => id !== row.id));
      dispatch(triggerQualityFrameworkRefresh());
    } catch (error) {
      console.error("Error deleting quality framework record:", error);
      setDeleteError(
        error?.message || "Unable to delete the record. Please try again.",
      );
    } finally {
      setDeletingId(null);
      setOpenActionMenuId(null);
    }
  };

  // Exports the selected rows, or every row currently visible after filters/search
  const handleDownloadResponse = async () => {
    const rowsToExport = selectedIds.length
      ? filteredRequests.filter((row) => selectedIds.includes(row.id))
      : filteredRequests;

    if (!rowsToExport.length) {
      return;
    }

    try {
      const exportBlob = await createExcelFile([
        {
          name: "Quality Framework",
          data: rowsToExport.map(mapQualityRequestForExport),
        },
      ]);

      if (!exportBlob) {
        return;
      }

      const timestamp = new Date().toISOString().split("T")[0];
      const link = document.createElement("a");
      link.href = URL.createObjectURL(exportBlob);
      link.download = `quality-framework-${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error exporting quality framework data:", error);
    }
  };

  const renderFilterOptions = (filterKey, label, formatValue = (value) => value) => {
    const options = filterOptions[filterKey];
    const allSelected = options.length > 0 && tempFilters[filterKey].length === options.length;

    return (
      <div className="qf-filter-accordion" key={filterKey}>
        <button type="button" className="qf-filter-accordion-header" onClick={() => toggleFilterAccordion(filterKey)}>
          <span>{label}</span>
          <NavDownArrow className={expandedFilters[filterKey] ? "expanded" : ""} width={18} height={18} color="#a4a3b1" />
        </button>
        {expandedFilters[filterKey] && (
          <div className="qf-filter-accordion-content">
            {options.length === 0 ? <span className="qf-filter-empty">No options available</span> : (
              <div className="qf-filter-options">
                <label className="qf-filter-option">
                  <input type="checkbox" checked={allSelected} onChange={() => toggleAllFilterValues(filterKey)} />
                  <span className="qf-filter-checkbox" />
                  <span>Select All</span>
                </label>
                {options.map((value) => (
                  <label className="qf-filter-option" key={value}>
                    <input type="checkbox" checked={tempFilters[filterKey].includes(value)} onChange={() => toggleFilterValue(filterKey, value)} />
                    <span className="qf-filter-checkbox" />
                    <span>{formatValue(value)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (rolesLoaded && !hasAccess) {
    return <QualityFrameworkAccessDenied />;
  }

  return (
    <div className="quality-framework-page" data-testid="quality-framework-page">
      {isLoading && (
        <LoadingIndicator
          show={true}
          fullscreen={false}
          transparent={true}
          className="quality-framework-loader"
        />
      )}

      {deleteError && (
        <MotifToast
          variant="error"
          position="top"
          onClose={() => setDeleteError("")}
        >
          {deleteError}
        </MotifToast>
      )}

      <div className="quality-framework-header">
        <h1 className="quality-framework-title">Quality Framework</h1>

        <div className="quality-framework-actions">
          <button
            type="button"
            className="quality-framework-button"
            onClick={handleDownloadResponse}
          >
            <DownloadIcon width={16} height={16} color="#FFFFFF" />
            <span>Download Response</span>
          </button>

          {canCreateRequest && (
            <button
              type="button"
              className="quality-framework-button primary"
              onClick={() => navigate(`/${routes.qualityFrameworkCreate}`)}
            >
                <PlayListPlusIcon width={16} height={16} color="#FFFFFF" />
              <span>Create New Request</span>
            </button>
          )}
        </div>
      </div>

      <div className="quality-framework-divider" />

      <div className="quality-framework-toolbar">
        <div className="quality-framework-tabs" role="tablist" aria-label="Request filters">
          {QUALITY_FRAMEWORK_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`quality-framework-tab ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => handleTabClick(tab.key)}
            >
              <span>{tab.label}</span>
              <span className="quality-framework-count">{tabCounts[tab.key] || 0}</span>
            </button>
          ))}
        </div>

        <div className="quality-framework-tools">
          <button
            type="button"
            className="quality-framework-filter-button"
            aria-label="Filter requests"
            aria-expanded={isFilterPanelOpen}
            onClick={toggleFilterPanel}
          >
            <FilterFunnelIcon width={20} height={20} color="#aab0cb" />
          </button>

          <label className="quality-framework-search" htmlFor="quality-framework-search-input">
            <SearchIcon width={20} height={20} color="#8f97bb" />
            <input
              id="quality-framework-search-input"
              type="text"
              placeholder="Search"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </label>
        </div>
      </div>

      {isFilterPanelOpen && (
        <aside className="qf-filter-panel" aria-label="Quality Framework filters">
          <div className="qf-filter-panel-header">
            <h2>Filters</h2>
            <button type="button" onClick={clearFilters}>Clear</button>
          </div>
          <p className="qf-filter-panel-subtitle">Select filters below</p>
          <div className="qf-filter-accordions">
            <div className="qf-filter-accordion">
              <button type="button" className="qf-filter-accordion-header" onClick={() => toggleFilterAccordion("dateRange")}>
                <span>Date Range</span>
                <NavDownArrow className={expandedFilters.dateRange ? "expanded" : ""} width={18} height={18} color="#a4a3b1" />
              </button>
              {expandedFilters.dateRange && (
                <div className="qf-filter-accordion-content qf-date-range">
                  <MotifDatePicker
                    range
                    value={[tempFilters.startDate, tempFilters.endDate]}
                    format="dd/MM/yyyy"
                    locale="en-US"
                    calendarPlacement="top-start"
                    yearsAfter={5}
                    yearsBefore={5}
                    onChange={handleDateChange}
                    dayPlaceholder="DD"
                    monthPlaceholder="MM"
                    yearPlaceholder="YYYY"
                    clearIcon={tempFilters.startDate || tempFilters.endDate ? <CloseIcon width={16} height={16} color="#A4A3B1" /> : null}
                    calendarIcon={<CalendarIcon width={16} height={16} color="#A4A3B1" />}
                  />
                </div>
              )}
            </div>
            {renderFilterOptions("assignedTo", "Assigned to")}
            {renderFilterOptions("clientName", "Client Name")}
            {renderFilterOptions("engagementName", "Engagement Name")}
            {renderFilterOptions("axonId", "AXON ID", (value) => `AX-${value}`)}
            {renderFilterOptions("superRegion", "Super Region")}
            {renderFilterOptions("country", "Region / Country")}
          </div>
          <div className="qf-filter-panel-footer">
            <div className="qf-filter-selected-info">
              <button type="button" aria-label="Clear selected filters" onClick={clearFilters}><CloseIcon width={14} height={14} color="#dedee2" /></button>
              <span>Selected {selectedFilterCount} items</span>
            </div>
            <div className="qf-filter-panel-actions">
              <button type="button" className="qf-filter-cancel-button" onClick={toggleFilterPanel}>Cancel</button>
              <button type="button" className="qf-filter-apply-button" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        </aside>
      )}

      <div className="quality-framework-content" role="region" aria-live="polite">
        {filteredRequests.length === 0 ? (
          <div className="quality-framework-empty-state">
            <PageStarIcon width={24} height={24} color="#c2c7e0" />
            <p className="quality-framework-empty-text">No request found</p>
          </div>
        ) : (
          <div className="qf-table-wrapper">
            <table className="qf-table">
              <thead>
                <tr>
                  <th className="qf-th qf-col-check">
                    <div className="qf-check-cell">
                      <input
                        type="checkbox"
                        className="qf-checkbox"
                        checked={selectedIds.length === paginatedRequests.length && paginatedRequests.length > 0}
                        onChange={toggleAll}
                        aria-label="Select all"
                      />
                    </div>
                  </th>
                  <th className="qf-th">Order ID</th>
                  <th className="qf-th">Client Name</th>
                  <th className="qf-th">Engagement Name</th>
                  <th className="qf-th">Super Region</th>
                  <th className="qf-th">Region</th>
                  <th className="qf-th">Analyzer type</th>
                  <th className="qf-th">Preparer</th>
                  <th className="qf-th">Reviewer</th>
                  <th className="qf-th">Assigned to</th>
                  <th className="qf-th">Analyzer Delivery Date</th>
                  <th className="qf-th">Checks Status</th>
                  <th className="qf-th qf-col-action">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRequests.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`qf-tr${selectedIds.includes(row.id) ? " selected" : ""}`}
                  >
                    <td className="qf-td qf-col-check">
                      <div className="qf-check-cell">
                        <input
                          type="checkbox"
                          className="qf-checkbox"
                          checked={selectedIds.includes(row.id)}
                          onChange={() => toggleSelect(row.id)}
                          aria-label={`Select row ${index + 1}`}
                        />
                      </div>
                    </td>
                    <td className="qf-td qf-order-id">AX-{row.orderRequestId}</td>
                    <td className="qf-td">
                      {renderTruncatedCell(row.clientName)}
                    </td>
                    <td className="qf-td">
                      {renderTruncatedCell(row.engagementName)}
                    </td>
                    <td className="qf-td">{row.region}</td>
                    <td className="qf-td">{row.countryName}</td>
                    <td className="qf-td">{row.analyzerType}</td>
                    <td className="qf-td">{renderTruncatedCell(row.preparer)}</td>
                    <td className="qf-td">{renderTruncatedCell(row.reviewer)}</td>
                    <td className="qf-td">
                      {renderTruncatedCell(row.assignedTo)}
                    </td>
                    <td className="qf-td">
                      {formatDateForEditDisplay(row.deliveryDate) || "-"}
                    </td>
                    <td className="qf-td">
                      <span
                        className={`qf-status qf-status--${row.status.toLowerCase()}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="qf-td qf-col-action">
                      <div className="qf-action-menu-wrapper">
                        <button
                          className="qf-action-icon-btn"
                          type="button"
                          aria-label={`Actions for order ${row.orderRequestId}`}
                          aria-haspopup="menu"
                          aria-expanded={openActionMenuId === row.id}
                          onClick={() =>
                            setOpenActionMenuId((prev) =>
                              prev === row.id ? null : row.id,
                            )
                          }
                        >
                          <MoreVertical width={16} height={16} color="#A4A3B1" />
                        </button>

                        {openActionMenuId === row.id && (
                          <div className="qf-action-menu" role="menu">
                            <button
                              type="button"
                              role="menuitem"
                              className="qf-action-menu-item"
                              onClick={() => {
                                setOpenActionMenuId(null);
                                navigate(
                                  `/${routes.qualityFrameworkCheckList}`,
                                  {
                                    state: {
                                      orderRequestId: row.orderRequestId,
                                      status: row.status,
                                      orderDetails: {
                                        axonId: row.axonId,
                                        clientName: row.clientName,
                                        engagementName: row.engagementName,
                                        countryName: row.countryName,
                                        analyzerType: row.analyzerType,
                                      },
                                    },
                                  },
                                );
                              }}
                            >
                              {row.canViewResponse
                                ? "Review Details"
                                : "Send Details"}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="qf-action-menu-item qf-action-menu-item--danger"
                              disabled={deletingId === row.orderRequestId}
                              onClick={() => handleDeleteRecord(row)}
                            >
                              {deletingId === row.orderRequestId
                                ? "Deleting..."
                                : "Delete"}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {filteredRequests.length > 0 && (
          <div className="qf-pagination">
            <div className="qf-pagination-section">
              <div className="qf-page-size-selector">
                <span>Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="qf-pagination-controls">
                <button
                  className="qf-pagination-button"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  «
                </button>
                <button
                  className="qf-pagination-button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <span className="qf-page-info">
                  {currentPage} / {totalPages || 1}
                </span>
                <button
                  className="qf-pagination-button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  ›
                </button>
                <button
                  className="qf-pagination-button"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  »
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default QualityFramework;
